import { Icon, IconName } from '@ardoq/icons';
import { space } from '@ardoq/design-tokens';
import { Checkbox } from '@ardoq/forms';
import { caption } from '@ardoq/typography';
import styled from 'styled-components';
import { GhostButton, SmallGhostButton } from '@ardoq/button';
import { ComponentType, ReactNode } from 'react';
import { FlexBox } from '@ardoq/layout';
import {
  DefaultNotification,
  InfoNotification,
  NotificationProps,
  WarningNotification,
} from './notifications';

type ClosableNotificationStatus = 'neutral' | 'info' | 'warning';

const STATUS_NOTIFICATIONS: Record<
  ClosableNotificationStatus,
  ComponentType<NotificationProps>
> = {
  neutral: DefaultNotification,
  info: InfoNotification,
  warning: WarningNotification,
};

type ClosableNotificationMode =
  | {
      mode?: 'dontShowAgain';
      dontShowAgain: boolean;
      setDontShowAgain: (value: boolean) => void;
    }
  | {
      mode: 'closeOnly';
    };

type ClosableNotificationProps = ClosableNotificationMode & {
  dismissNotification: VoidFunction;
  children: ReactNode;
  status?: ClosableNotificationStatus;
  /** Accessible name for the close button. */
  closeButtonLabel: string;
};

export const ClosableNotification = ({
  children,
  dismissNotification,
  status = 'neutral',
  closeButtonLabel,
  ...modeProps
}: ClosableNotificationProps) => {
  const StatusNotification = STATUS_NOTIFICATIONS[status];
  const isCloseOnly = modeProps.mode === 'closeOnly';
  const CloseButton = isCloseOnly ? SmallGhostButton : GhostButton;
  const closeButton = (
    <CloseButton aria-label={closeButtonLabel} onClick={dismissNotification}>
      <Icon iconName={IconName.CLOSE} />
    </CloseButton>
  );

  return (
    <StatusNotification alignItems="center">
      <FlexBox gap={isCloseOnly ? 'medium' : 'small'} align="center">
        <BannerText>{children}</BannerText>
        {!isCloseOnly && (
          <Checkbox
            isChecked={modeProps.dontShowAgain}
            onChange={modeProps.setDontShowAgain}
          >
            <CheckboxLabel>Don&apos;t show this again</CheckboxLabel>
          </Checkbox>
        )}
        {isCloseOnly ? (
          <CompactCloseButtonContainer>
            {closeButton}
          </CompactCloseButtonContainer>
        ) : (
          closeButton
        )}
      </FlexBox>
    </StatusNotification>
  );
};

const CompactCloseButtonContainer = styled.div`
  margin: -${space.xsmall} 0;
`;

const BannerText = styled.span`
  margin-right: auto;
`;

const CheckboxLabel = styled.span`
  ${caption};
  white-space: nowrap;
`;
